import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  MenuItem,
  Select,
  TextField,
  Grid,
  CircularProgress,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import FormControl from '@mui/material/FormControl';
import dayjs from 'dayjs';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Image, Layer, Stage } from 'react-konva';
import { ReactComponent as CloseIcon } from '../../../../assets/accountSetting-assets/x-circle.svg';
import DataFieldDropdown from './DataFieldDropdown';
import Toast from '../../utils/Toast';
import { lightPalette } from '../../../../theme';
import CustomTooltip from '../../utils/CustomTooltip';
import {
  DataFieldDropdownContainer,
  IconButtonContainer,
  InputButtonContainer,
  InputButtonIconsContainer,
  InputPopupCloseIconContainer,
  InputPopupContainer,
  InputRemoveButton,
  CurrencyConverterBoxContainer,
  LoaderBox,
  ProductGrid,
  ValidateButton,
  ValidationMessageTypo,
  ValidationTypoWithMB,
  PreviewCropImageMainBox,
  PreviewCropImageTypoBox,
  classes,
  StyledDatePicker,
  SelectCurrencyAutoComplete,
  StyledBackdrop,
  ConvertCurrencyTypo,
} from './utils';
import { ANNOTATION_TOAST } from '../../../../constants/toastConstant';
import { getCountries } from '../../../../services/addMemberApi';
import { postCurrencyConverter } from '../../../../services/annotation';
import { ANNOTATION } from '../../../../constants/staticContent';
import { dateFormatConverter, isValidDate } from './dateFormatter';

const Input = ({
  openedData,
  getOpenedData,
  selectedId,
  setSelectedId,
  updateOpenedData,
  removeAnnotation,
  checkedDataFields,
  setCheckedDataFields,
  data,
  setOpenedData,
  setData,
  products,
  changedData,
  versionId,
  isEditing,
  setTransformBox,
  setDialogOpen,
  setNewAnnotation,
  setTooltipId,
  originalImageSize,
  selectedVersion,
  popUpLoader,
  selectedBox,
  locationData,
  imageUrl,
  currentPageNumber,
}) => {
  const [label, setLabel] = useState('');
  const [value, setValue] = useState(null);
  const [date, setDate] = useState('');
  const [isValidInput, setIsValidInput] = useState(true);
  const [isDataFieldSelected, setIsDataFieldSelected] = useState(true);
  const [boxLoader, setBoxLoader] = useState(true);
  const [isDate, setIsDate] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [currencyOption, setCurrencyOptions] = useState([]);
  const [getCountriesAPIRes, setGetCountriesAPIRes] = useState([]);
  const [open, setOpen] = useState(false);
  const [isCurrencyConvert, setIsCurrencyConvert] = useState(false);
  const [currentCurrency, setCurrentCurrency] = useState({
    key: 'USD',
    label: 'USD',
  });
  const [previousTargetCurrency, setPreviousTargetCurrency] = useState('');

  const fetchData = async () => {
    try {
      const response = await getCountries();
      const targetCurrencyOption = response?.map((item, index) => ({
        key: index,
        label: item.currency,
      }));
      setGetCountriesAPIRes(targetCurrencyOption);
      return targetCurrencyOption;
    } catch (error) {
      return error;
    }
  };
  useEffect(() => {
    const setCurrencyOptionFun = async () => {
      const response = await fetchData();
      if (response) {
        const currencyOptionsFiltered = response?.filter(
          (option) => option.label !== currentCurrency.label,
        );
        setCurrencyOptions(
          currencyOptionsFiltered.map((item, index) => ({
            key: index,
            label: item.label,
          })),
        );
      }
      if (locationData) {
        setCurrency(locationData);
      }
    };
    setCurrencyOptionFun();
  }, []);

  const handleCurrencyChange = (event, newValue) => {
    if (newValue) {
      setCurrency(newValue.label);
    }
  };

  useEffect(() => {
    if (getCountriesAPIRes) {
      if (previousTargetCurrency) {
        const currencyOptionsFiltered = getCountriesAPIRes.filter(
          (option) => option.label !== previousTargetCurrency.label,
        );
        setCurrency(currencyOptionsFiltered[0]?.label);
        setCurrencyOptions(
          currencyOptionsFiltered.map((item, index) => ({
            key: index,
            label: item.label,
          })),
        );
      } else {
        const currencyOptionsFiltered = getCountriesAPIRes.filter(
          (option) => option.label !== currentCurrency.label,
        );
        setCurrency(currencyOptionsFiltered[0]?.label);
        setCurrencyOptions(
          currencyOptionsFiltered.map((item, index) => ({
            key: index,
            label: item.label,
          })),
        );
      }
    }
  }, [currentCurrency, previousTargetCurrency]);

  const handleDatePickerChange = (newPaymentDate) => {
    const datePart = dayjs(newPaymentDate.$d); // Convert to dayjs object if necessary
    let formattedDateForPayment;
    let formattedDate;

    // Split datePart into date and time parts
    const formattedDatePart = datePart.format('YYYY-MM-DD HH:mm:ss');
    const [newDatePartString, newTimePart] = formattedDatePart.split(' ');
    const [datePartString] = openedData.value.split(' ');

    // Check for different formats and delimiters based on datePartString
    if (datePartString.includes('/')) {
      const parts = newDatePartString.split('-');
      // Assuming MM/DD/YYYY format
      formattedDateForPayment = `${parts[1]}/${parts[2]}/${parts[0]}`;

      const combinedDateTime = `${formattedDateForPayment} ${newTimePart || '00:00:00'}`;

      // Conditionally format date with or without time part
      if (newTimePart === '00:00:00' || !newTimePart) {
        formattedDate = dayjs(formattedDateForPayment, 'MM/DD/YYYY').format(
          'MM/DD/YYYY',
        );
      } else {
        formattedDate = dayjs(combinedDateTime, 'MM/DD/YYYY HH:mm').format(
          'MM/DD/YYYY HH:mm',
        );
      }
    } else if (datePartString.includes('-')) {
      const parts = newDatePartString.split('-');
      // Assuming YYYY-MM-DD format
      formattedDateForPayment = `${parts[0]}-${parts[1]}-${parts[2]}`;

      const combinedDateTime = `${formattedDateForPayment} ${newTimePart || '00:00:00'}`;

      // Conditionally format date with or without time part
      if (newTimePart === '00:00:00' || !newTimePart) {
        formattedDate = dayjs(formattedDateForPayment, 'YYYY-MM-DD').format(
          'DD-MMM-YYYY',
        );
      } else {
        formattedDate = dayjs(combinedDateTime, 'YYYY-MM-DD HH:mm').format(
          'DD-MMM-YYYY HH:mm',
        );
      }
    } else {
      // Handle other cases, assuming DD-MMM-YYYY format like 20-May-18
      const parts = newDatePartString.split('-');
      formattedDateForPayment = `${parts[2]}-${datePart.format('MMM')}-${parts[0]}`;

      // No time part for this format
      formattedDate = dayjs(formattedDateForPayment, 'DD-MMM-YYYY').format(
        'DD-MMM-YYYY',
      );
    }

    setValue(formattedDate);
    setDate(datePart); // Set date object as needed
    setIsValidInput(true);
  };
  useEffect(() => {
    if (selectedId) {
      getOpenedData(selectedId, currentPageNumber);
    }
  }, [selectedId, openedData]);

  useEffect(() => {
    if (openedData?.currentCurrency) {
      setPreviousTargetCurrency({
        key: openedData?.currentCurrency,
        label: openedData?.currentCurrency,
      });
    } else {
      setPreviousTargetCurrency('');
    }
  }, [openedData]);

  useEffect(() => {
    if (popUpLoader && openedData.label) {
      setBoxLoader(false);
      setLabel(openedData?.label);

      if (openedData?.label?.includes('Date')) {
        const formattedDateForPayment = dateFormatConverter(openedData.value);
        const [, timePart = '00:00:00'] = openedData.value.split(' '); // Extract date part only
        const parsedDate = dayjs(`${formattedDateForPayment} ${timePart}`);
        setDate(parsedDate); // Assuming setDate is a function to set the formatted date
      }
    } else {
      setBoxLoader(true);
      setLabel('');
      setDate('');
    }
    if (
      popUpLoader &&
      openedData.value !== undefined &&
      openedData.value !== null
    ) {
      const isValueDate = isValidDate(openedData.value);
      setIsDate(isValueDate);
      if (isValueDate) {
        setBoxLoader(false);
        const formattedDateForPayment = dateFormatConverter(openedData.value);
        const [, timePart = '00:00:00'] = openedData.value.split(' '); // Extract date part only
        const parsedDate = dayjs(
          `${formattedDateForPayment} ${timePart || '00:00:00'}`,
        );
        setDate(parsedDate); // Assuming setDate is a function to set the formatted date
        setValue(openedData?.value);
      } else {
        setBoxLoader(false);
        setValue(openedData?.value);
      }
    } else {
      setBoxLoader(false);
      setValue('');
    }
  }, [openedData, selectedId]);

  const onValueChange = (event) => {
    const inputValue = event?.target?.value;
    setValue(inputValue);
    setIsValidInput(inputValue.trim() !== '');
  };
  const handleClose = () => {
    if (openedData.label === undefined && openedData.value === undefined) {
      const removeData = data[0][currentPageNumber].filter(
        (item) => item.id !== selectedId,
      );
      setData(removeData);
      setSelectedId(null);
      setTransformBox(null);
      setNewAnnotation([]);
      setDialogOpen(false);
      setTooltipId('');
      // setPdfBox(null);
    } else {
      setSelectedId(null);
      setOpenedData({
        label: '',
        value: null,
      });
      // setPdfBox(null);
    }
  };

  const handleRemove = () => {
    removeAnnotation(selectedId);
    setOpenedData({
      label: '',
      value: null,
    });
  };

  const checkedDataFieldsData = Object.values(checkedDataFields).flatMap(
    (group) => group,
  );
  const productsFieldsData = products.flatMap((group) => group);

  const options = Object.values(checkedDataFields).reduce(
    (accumulator, response) => {
      const labelData = data.map((item) => item.label);

      response.forEach((dataField) => {
        if (
          !accumulator.includes(dataField?.short_desc) &&
          !labelData.includes(dataField?.annotation_field)
        ) {
          accumulator.push(dataField?.short_desc);
        }

        const matchingLabels = productsFieldsData.filter(
          (m) => m.label === dataField?.annotation_field,
        );
        const product = matchingLabels.map((matchingLabel) => ({
          ...matchingLabel,
          short_desc: dataField?.short_desc,
        }));

        accumulator = [...accumulator, product];
        const productArray = [];

        for (let i = 1; i <= products.length; i++) {
          productArray.push(i);
        }
        const newArray = accumulator.filter(
          (i) => typeof i !== 'string' && i.length > 0,
        );
        if (newArray.length !== response.length) {
          const ProductLabel = [
            ...new Set(productsFieldsData.map((p) => p.label)),
          ];
          const arrayLengths = newArray.filter(
            (innerArray) => innerArray.length !== products.length,
          );
          if (arrayLengths.length > 0) {
            const filteredArray = arrayLengths.map((subArray) => {
              const subarrayProducts = subArray.map((s) => s.product);
              return productArray.filter(
                (item) => !subarrayProducts.includes(item),
              );
            });

            const productList = filteredArray.flatMap((p) => p);
            const uniqueSet = new Set(productList);
            const uniqueArray = Array.from(uniqueSet);

            accumulator = [...accumulator, ...uniqueArray];
          } else {
            const isLabelRemain = response.filter(
              (r) =>
                r.group === 'Product' &&
                !ProductLabel.includes(r.annotation_field),
            );
            if (isLabelRemain.length > 0) {
              accumulator = [...accumulator, ...productArray];
            }
          }
        }
      });
      return accumulator;
    },
    [],
  );

  const ProductArrayList = [];

  const uniqueNumbers = [
    ...new Set(options.flat().filter((item) => typeof item === 'number')),
  ].sort((a, b) => a - b);
  ProductArrayList.push(...uniqueNumbers);

  const [selectedProduct, setSelectedProduct] = useState(
    ProductArrayList.length > 0 ? ProductArrayList[0] : '',
  );

  const IsDataExist = [
    ...checkedDataFieldsData
      .filter((item) => item.short_desc === label)
      .map((t) =>
        t.annotation_field !== null ? t.annotation_field : t.short_desc,
      ),
  ];

  const handleConfirm = () => {
    const isInputEmpty = value?.toString()?.trim() === '';
    if (!label && isInputEmpty) {
      setIsValidInput(value.trim() !== '');
      setIsDataFieldSelected(false);
      return;
    }
    if (!label) {
      setIsDataFieldSelected(false);
      return;
    }
    if (isInputEmpty) {
      setIsValidInput(value.trim() !== '');
      return;
    }
    let newLabel;
    if (label !== null && label !== openedData.label) {
      const items = checkedDataFieldsData.filter(
        (elem) => elem.short_desc === label,
      );
      newLabel = items[0].annotation_field;
    } else {
      newLabel = label;
    }
    const mergedData = Object.keys(data[0]).reduce(
      (acc, key) => [...acc, ...data[0][key]],
      [],
    );
    if (
      label !== null &&
      checkedDataFieldsData
        .filter((item) => !item.short_desc.startsWith('Product'))
        .map((item) => item.short_desc)
        .includes(label)
    ) {
      if (
        value !== '' &&
        openedData.label === undefined &&
        IsDataExist.length > 0 &&
        mergedData.map((item) => item.label).includes(...IsDataExist)
      ) {
        const existingItem = mergedData.find((item) =>
          IsDataExist.includes(item.label),
        );
        if (existingItem) {
          const newData = JSON.parse(JSON.stringify(data));
          const firstPageObj = newData[0];
          Object.keys(firstPageObj).forEach((pageKey) => {
            firstPageObj[pageKey] = firstPageObj[pageKey].filter(
              (item) => item.id !== existingItem.id,
            );
          });
          setData(newData);
          const confirmOpenedData = openedData;
          confirmOpenedData.x /= originalImageSize.width / imageUrl.width;
          confirmOpenedData.y /= originalImageSize.height / imageUrl.height;
          confirmOpenedData.width /= originalImageSize.width / imageUrl.width;
          confirmOpenedData.height /=
            originalImageSize.height / imageUrl.height;
          confirmOpenedData.label = newLabel;
          confirmOpenedData.value = value;
          confirmOpenedData.validated = true;
          confirmOpenedData.version = selectedVersion;
          confirmOpenedData.short_desc = label;
          confirmOpenedData.section = existingItem.section;
          if (isCurrencyConvert) {
            confirmOpenedData.currentCurrency = currentCurrency?.value;
          }

          // Remove the 'color' key
          delete confirmOpenedData.color;
          updateOpenedData(confirmOpenedData, newData);
          setSelectedId(null);
          // setPdfBox(null);
          setTransformBox(null);
          setTooltipId('');
          Toast({
            message: ANNOTATION_TOAST.DATA_OVERRIDE,
            variant: 'success',
          });
        }
      } else if (value !== '') {
        const confirmOpenedData = openedData;
        if (!confirmOpenedData.label) {
          confirmOpenedData.x /= originalImageSize.width / imageUrl.width;
          confirmOpenedData.y /= originalImageSize.height / imageUrl.height;
          confirmOpenedData.width /= originalImageSize.width / imageUrl.width;
          confirmOpenedData.height /=
            originalImageSize.height / imageUrl.height;
          confirmOpenedData.label = newLabel;
          confirmOpenedData.value = value;
          confirmOpenedData.validated = true;
          confirmOpenedData.version = selectedVersion;
          confirmOpenedData.short_desc = label;
          if (isCurrencyConvert) {
            confirmOpenedData.currentCurrency = currentCurrency?.value;
          }

          // Remove the 'color' key
          delete confirmOpenedData.color;
        } else {
          confirmOpenedData.label = newLabel;
          confirmOpenedData.short_desc = label;
          confirmOpenedData.value = value;
          confirmOpenedData.validated = true;
          confirmOpenedData.version = selectedVersion;
          if (isCurrencyConvert) {
            confirmOpenedData.currentCurrency = currentCurrency?.value;
          }
        }

        updateOpenedData(confirmOpenedData);
        setSelectedId(null);
        // setPdfBox(null);
        setTransformBox(null);
        setTooltipId('');
        Toast({ message: ANNOTATION_TOAST.DATA_CONFIRMED, variant: 'success' });
      }
    } else {
      const confirmOpenedData = openedData;
      if (value !== '' && openedData.label !== undefined) {
        confirmOpenedData.label = newLabel;
        confirmOpenedData.short_desc = label;
        confirmOpenedData.value = value;
        confirmOpenedData.validated = true;
        confirmOpenedData.version = selectedVersion;
        if (isCurrencyConvert) {
          confirmOpenedData.currentCurrency = currentCurrency?.value;
        }
        updateOpenedData(confirmOpenedData);
        setSelectedId(null);
        // setPdfBox(null);
        setTransformBox(null);
        setTooltipId('');
        Toast({ message: ANNOTATION_TOAST.DATA_CONFIRMED, variant: 'success' });
      } else if (
        openedData.label === undefined &&
        label !== null &&
        checkedDataFieldsData
          .filter((item) => item.short_desc.startsWith('Product'))
          .map((item) => item.short_desc)
          .includes(label)
      ) {
        if (
          value !== '' &&
          IsDataExist.length > 0 &&
          !mergedData
            .map((item) => item.product === selectedProduct && item.label)
            .includes(...IsDataExist)
        ) {
          confirmOpenedData.x /= originalImageSize.width / imageUrl.width;
          confirmOpenedData.y /= originalImageSize.height / imageUrl.height;
          confirmOpenedData.width /= originalImageSize.width / imageUrl.width;
          confirmOpenedData.height /=
            originalImageSize.height / imageUrl.height;
          confirmOpenedData.label = newLabel;
          confirmOpenedData.value = value;
          confirmOpenedData.product = selectedProduct;
          confirmOpenedData.validated = true;
          confirmOpenedData.version = selectedVersion;
          confirmOpenedData.short_desc = label;
          if (isCurrencyConvert) {
            confirmOpenedData.currentCurrency = currentCurrency?.value;
          }

          // Remove the 'color' key
          delete confirmOpenedData.color;
          updateOpenedData(confirmOpenedData);
          setSelectedId(null);
          // setPdfBox(null);
          setTransformBox(null);
          setTooltipId('');
          Toast({
            message: ANNOTATION_TOAST.DATA_CONFIRMED,
            variant: 'success',
          });
        }
      }
    }
  };

  const handleProductNoChange = (event) => {
    setSelectedProduct(event.target.value);
  };

  const handleConverter = async (convertingValue) => {
    setOpen(true);
    let numberWithoutCommas;
    if (typeof convertingValue === 'number') {
      numberWithoutCommas = convertingValue;
    } else if (
      typeof convertingValue === 'string' &&
      (convertingValue?.includes(',') ||
        convertingValue?.includes('$') ||
        convertingValue?.includes(' '))
    ) {
      numberWithoutCommas = parseFloat(convertingValue.replace(/[$,\s]/g, ''));
    } else {
      numberWithoutCommas = parseFloat(convertingValue);
    }
    setPreviousTargetCurrency('');
    setCurrentCurrency({ value: currency, label: currency });
    const targetCurrency = await postCurrencyConverter(
      previousTargetCurrency?.label || currentCurrency?.label,
      currency,
      numberWithoutCommas,
    );
    if (targetCurrency) {
      setIsCurrencyConvert(true);
      setValue(targetCurrency.data.response);
      setOpen(false);
    } else {
      setIsCurrencyConvert(false);
    }
  };

  // useEffect(() => {
  //   if (selectedId !== null && isPdf) {
  //     const pdfBoxCoordinates = {
  //       ...selectedBox,
  //       x: selectedBox.x / 2.8,
  //       y: selectedBox.y / 2.8,
  //       width: (selectedBox.width / 2.8) + 10,
  //       height: (selectedBox.height / 2.8) + 10,
  //     };
  //     setPdfBox(pdfBoxCoordinates);
  //   }
  // }, [isPdf, selectedId]);

  const getValidationMessage = (
    isOpenedData,
    labels,
    dataFields,
    dataExist,
    dataForValidate,
    product,
  ) => {
    if (
      isOpenedData.label === undefined &&
      labels !== null &&
      dataFields
        .filter((item) => !item.short_desc.startsWith('Product'))
        .map((item) => item.short_desc)
        .includes(labels)
    ) {
      if (
        dataExist.length > 0 &&
        dataForValidate.map((item) => item.label).includes(...dataExist)
      ) {
        return (
          <ValidationMessageTypo>
            {ANNOTATION.VALIDATION_MESSAGE.OVERRIDE_VALUE}
          </ValidationMessageTypo>
        );
      }
    } else if (
      isOpenedData.label === undefined &&
      labels !== null &&
      dataFields
        .filter((item) => item.short_desc.startsWith('Product'))
        .map((item) => item.short_desc)
        .includes(labels)
    ) {
      if (
        dataExist.length > 0 &&
        dataForValidate
          .map((item) => item.product === product && item.label)
          .includes(...dataExist)
      ) {
        return (
          <ValidationMessageTypo>
            {ANNOTATION.VALIDATION_MESSAGE.EXISTING_VALUE} {product},{' '}
            {ANNOTATION.VALIDATION_MESSAGE.ANOTHER_SELECTION}
          </ValidationMessageTypo>
        );
      }
    }
    return null;
  };

  return (
    <Dialog
      open={selectedId !== null}
      onClose={
        openedData.label !== undefined && openedData.value !== undefined
          ? handleClose
          : null
      }
      maxWidth="md"
    >
      <InputPopupContainer>
        {openedData.label !== undefined && openedData.value !== undefined && (
          <IconButtonContainer aria-label="close" onClick={handleClose}>
            <InputPopupCloseIconContainer>
              <CloseIcon />
            </InputPopupCloseIconContainer>
          </IconButtonContainer>
        )}
        {open ? (
          <Box>
            <StyledBackdrop open={open}>
              <CircularProgress color="inherit" />
            </StyledBackdrop>
          </Box>
        ) : null}
        {boxLoader ? (
          <LoaderBox>
            <CircularProgress />
          </LoaderBox>
        ) : null}

        <Grid container columnGap={1}>
          <Grid item xs={7}>
            {!boxLoader && (
              <DataFieldDropdownContainer>
                <DataFieldDropdown
                  label={label}
                  setLabel={setLabel}
                  checkedDataFields={checkedDataFields}
                  setCheckedDataFields={setCheckedDataFields}
                  data={data[0][currentPageNumber]}
                  openedData={openedData}
                  products={products}
                  changedData={changedData}
                  setIsDataFieldSelected={setIsDataFieldSelected}
                  versionId={versionId}
                  isEditing={isEditing}
                  isDate={isDate}
                  currentPageNumber={currentPageNumber}
                />

                {getValidationMessage(
                  openedData,
                  label,
                  checkedDataFieldsData,
                  IsDataExist,
                  data,
                  selectedProduct,
                )}
                {!isDataFieldSelected && (
                  <ValidationMessageTypo>
                    {
                      ANNOTATION.INVOICE_ANNOTATION_MESSAGE
                        .SELECT_DATA_FIELD_VALIDATION
                    }
                  </ValidationMessageTypo>
                )}
              </DataFieldDropdownContainer>
            )}

            {!boxLoader &&
              openedData.label === undefined &&
              label?.startsWith('Product') && (
                <ProductGrid container>
                  <Grid item xs={12} lg={3.5} mb={{ xs: '10px', lg: 0 }}>
                    <Select
                      size="small"
                      value={selectedProduct}
                      onChange={handleProductNoChange}
                      fullWidth
                    >
                      {ProductArrayList.map((item) => (
                        <MenuItem key={item} value={item}>
                          Product {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <CustomTooltip
                      title={data
                        .map(
                          (item) =>
                            item.product === selectedProduct && item.value,
                        )
                        .filter((f) => f !== false)}
                    >
                      <TextField
                        disabled
                        InputProps={{
                          readOnly: true,
                        }}
                        fullWidth
                        size="small"
                        id="filled-basic"
                        value={data
                          .map(
                            (item) =>
                              item.product === selectedProduct && item.value,
                          )
                          .filter((f) => f !== false)}
                      />
                    </CustomTooltip>
                  </Grid>
                </ProductGrid>
              )}
            {!boxLoader && openedData.product !== undefined && (
              <ProductGrid container>
                <Grid item xs={12}>
                  <CustomTooltip
                    title={
                      openedData.label === 'Description'
                        ? `Product ${openedData.product} - ${openedData.value}`
                        : data
                            .filter(
                              (item) =>
                                item.product === openedData.product &&
                                item.label === 'Description' &&
                                item.value,
                            )
                            .map(
                              (item) =>
                                `Product ${item.product} - ${item.value}`,
                            )
                            .join(' ') || 'No Description Available'
                    }
                  >
                    <TextField
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      fullWidth
                      size="small"
                      id="filled-basic"
                      value={
                        openedData.label === 'Description'
                          ? `Product ${openedData.product} - ${openedData.value}`
                          : data
                              .filter(
                                (item) =>
                                  item.product === openedData.product &&
                                  item.label === 'Description' &&
                                  item.value,
                              )
                              .map(
                                (item) =>
                                  `Product ${item.product} - ${item.value}`,
                              )
                              .join(' ') || 'No Description Available'
                      }
                    />
                  </CustomTooltip>
                </Grid>
              </ProductGrid>
            )}
            {!boxLoader &&
            (label?.startsWith('Payment') || label === 'Product net Price') ? (
              <CurrencyConverterBoxContainer>
                <FormControl size="small">
                  <SelectCurrencyAutoComplete
                    id="demo-select-small"
                    options={[currentCurrency]}
                    getOptionLabel={(option) => option.label}
                    value={previousTargetCurrency || currentCurrency}
                    disabled
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                  />
                </FormControl>
                <FormControl size="small">
                  <SelectCurrencyAutoComplete
                    id="currency-autocomplete"
                    options={currencyOption}
                    getOptionLabel={(option) => option.label}
                    value={currencyOption[0]}
                    onChange={handleCurrencyChange}
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                  />
                </FormControl>
                <ConvertCurrencyTypo
                  color={lightPalette.primary.main}
                  onClick={() => handleConverter(value)}
                >
                  Convert Currency
                </ConvertCurrencyTypo>
              </CurrencyConverterBoxContainer>
            ) : null}

            {!boxLoader && (label?.includes('Date') || isDate) && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePicker
                  className={classes.inputGrid}
                  disabled={versionId !== '' && isEditing === null}
                  required
                  reduceAnimations
                  format="MM/DD/YYYY HH:mm"
                  slotProps={{
                    textField: {
                      readOnly: true,
                      size: 'small',
                    },
                  }}
                  PopoverProps={classes.datePickerTextField}
                  value={date || null}
                  onChange={handleDatePickerChange}
                  views={['year', 'month', 'day', 'hours', 'minutes']}
                />
              </LocalizationProvider>
            )}
            {!boxLoader && !(label?.includes('Date') || isDate) && (
              <>
                <TextField
                  fullWidth
                  disabled={versionId !== '' && isEditing === null}
                  multiline
                  maxRows={4}
                  id="outlined-size-small"
                  value={value}
                  size="small"
                  onChange={onValueChange}
                />
                <ValidationTypoWithMB>
                  {!isValidInput ? 'Please fill out this field.' : null}
                </ValidationTypoWithMB>
              </>
            )}
            <InputButtonContainer>
              <ValidateButton
                disabled={versionId !== '' && isEditing === null}
                onClick={handleConfirm}
              >
                <InputButtonIconsContainer>
                  <CheckOutlinedIcon />
                </InputButtonIconsContainer>
                Validate
              </ValidateButton>

              <InputRemoveButton
                disabled={versionId !== '' && isEditing === null}
                onClick={handleRemove}
              >
                <InputButtonIconsContainer>
                  <ClearOutlinedIcon />
                </InputButtonIconsContainer>
                Remove
              </InputRemoveButton>
            </InputButtonContainer>
          </Grid>
          <Divider orientation="vertical" flexItem />
          {selectedBox !== null && !boxLoader && (
            <Grid item xs={4.5}>
              <PreviewCropImageTypoBox>
                <Typography variant="h5">Bounding Box Preview</Typography>
              </PreviewCropImageTypoBox>
              <PreviewCropImageMainBox>
                {/* {isPdf
                    && (
                      <Stage
                        height={Math.abs((selectedBox.height / 2.8))
                    * (originalImageSize.height / imageUrl.height) + 10}
                        width={Math.abs((selectedBox.width / 2.8))
                    * (originalImageSize.width / imageUrl.width) + 10}
                      >
                        <Layer>
                          <Image
                            image={imageUrl}
                            height={Math.abs((selectedBox.height / 2.8))
                        * (originalImageSize.height / imageUrl.height) + 10}
                            width={Math.abs((selectedBox.width / 2.8))
                        * (originalImageSize.width / imageUrl.width) + 10}
                            crop={pdfBox}
                          />
                        </Layer>
                      </Stage>
                    )} */}
                {/* {!isPdf && ( */}
                <Stage
                  height={
                    Math.abs(selectedBox?.height) *
                    (originalImageSize.height / imageUrl.height)
                  }
                  width={
                    Math.abs(selectedBox?.width) *
                    (originalImageSize.width / imageUrl.width)
                  }
                >
                  <Layer>
                    <Image
                      image={imageUrl}
                      height={
                        Math.abs(selectedBox?.height) *
                        (originalImageSize.height / imageUrl.height)
                      }
                      width={
                        Math.abs(selectedBox?.width) *
                        (originalImageSize.width / imageUrl.width)
                      }
                      crop={selectedBox}
                    />
                  </Layer>
                </Stage>
                {/* )} */}
              </PreviewCropImageMainBox>
            </Grid>
          )}
        </Grid>
      </InputPopupContainer>
    </Dialog>
  );
};

const dataObjectPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  group: PropTypes.string.isRequired,
  short_desc: PropTypes.string.isRequired,
  annotation_field: PropTypes.string,
});

Input.defaultProps = {
  versionId: '',
  isEditing: null,
  setTransformBox: null,
  selectedBox: {
    label: undefined,
    value: undefined,
    product: undefined,
  },
  setNewAnnotation: [],
  setTooltipId: '',
  originalImageSize: {
    width: '',
    height: '',
  },
};

Input.propTypes = {
  openedData: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    product: PropTypes.number,
    currentCurrency: PropTypes.string,
  }).isRequired,
  getOpenedData: PropTypes.func.isRequired,
  setOpenedData: PropTypes.func.isRequired,
  selectedId: PropTypes.string.isRequired,
  locationData: PropTypes.string.isRequired,
  setSelectedId: PropTypes.func.isRequired,
  updateOpenedData: PropTypes.func.isRequired,
  removeAnnotation: PropTypes.func.isRequired,
  checkedDataFields: PropTypes.shape({
    Invoice: PropTypes.arrayOf(dataObjectPropTypes),
    Supplier: PropTypes.arrayOf(dataObjectPropTypes),
    Payment: PropTypes.arrayOf(dataObjectPropTypes),
    Product: PropTypes.arrayOf(dataObjectPropTypes),
    Payment_Method: PropTypes.arrayOf(dataObjectPropTypes),
  }).isRequired,
  setCheckedDataFields: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      label: PropTypes.string,
      value: PropTypes.string,
      product: PropTypes.number,
    }),
  ).isRequired,
  setData: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        product: PropTypes.number.isRequired,
      }),
    ),
  ).isRequired,
  changedData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      label: PropTypes.string,
      value: PropTypes.string,
      product: PropTypes.number,
    }),
  ).isRequired,
  versionId: PropTypes.string,
  isEditing: PropTypes.bool,
  setTransformBox: PropTypes.func,
  selectedBox: PropTypes.shape({
    id: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    product: PropTypes.number,
  }),
  setDialogOpen: PropTypes.func.isRequired,
  setNewAnnotation: PropTypes.func,
  setTooltipId: PropTypes.func,
  originalImageSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  selectedVersion: PropTypes.string.isRequired,
  popUpLoader: PropTypes.bool.isRequired,
  currentPageNumber: PropTypes.string.isRequired,
  imageUrl: PropTypes.shape({
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }).isRequired,
};

export default Input;
